import React from 'react';
import { IconJet, IconCar, IconEarth, IconMoney, IconHotel, IconSpeedometer, IconPlane, IconHalfmoon, IconCheckCurved, IconTrips, IconEco, IconInfo } from '@amtrav/icons';

/* eslint-disable max-len */

// These colors are specifically reserved
// for Reports Chart icons & graphs
const ColorPalette = {
  amazonite: '#12c5b2',
  azureishWhite: '#CEEAEF',
  blond: '#FCEFC4',
  blue: '#208dcc',
  cgBlue: '#007da5',
  cherry: '#db3260',
  cyan: '#4eb4c7',
  green: '#43AC6A',
  lavenderGray: '#C6C0DD',
  liberty: '#6353a3',
  lightBlue: '#ADD5EC',
  mantis: '#75cb44',
  nadeshikoPink: '#F1B4C5',
  orange: '#e87e04',
  paleCerulean: '#A2CFDE',
  paleTurquoise: '#BEEFEA',
  peachPuff: '#F8DCBC',
  saffron: '#F7CB2C',
  seaFoamGreen: '#BAE0C8',
  teaGreen: '#CCECBB'
};
const GreyPalette = {
  // commented numbers are for GENERIC_COLORS - gray
  // which correspond to neutral in theme-system
  50: '#fafafa',
  75: '#f7f7f7',
  // 100
  100: '#f5f5f5',
  // 200
  200: '#eeeeee',
  // 300
  300: '#d6d6d6',
  // 400
  400: '#c0c0c0',
  // 500
  500: '#9e9e9e',
  // 600
  600: '#757575',
  // 700
  700: '#616161',
  // 800
  800: '#424242',
  // 900
  900: '#212121' // 950
};
const breakpoint = {
  small: 480,
  medium: 768,
  large: 960,
  xlarge: 1280
};

// Icons colors used for base are amtrav colors.
var BaseGlobal = {
  greyPalette: {
    ...GreyPalette
  },
  // our new theme-system does not have global 'primary' & 'secondary' colors
  'primary--color': '#3B82F6',
  // THEME_TOKENS.base.color.action
  'secondary--color': GreyPalette['900'],
  // THEME_TOKENS.base.color.neutral950
  'tertiary--color': GreyPalette['500'],
  'danger--color': '#EF4444',
  // THEME_TOKENS.base.color.danger
  'warning--color': '#D97706',
  // THEME_TOKENS.base.color.warning
  'success--color': '#22C55E',
  // THEME_TOKENS.base.color.success
  'info--color': '#1D4ED8',
  // THEME_TOKENS.base.color.info
  'light--color': GreyPalette['75'],
  'accent--color': GreyPalette['200'],
  'disabled--color': GreyPalette['400'],
  'dark--color': GreyPalette['900'],
  // THEME_TOKENS.base.color.neutral950

  'border--color': GreyPalette['300'],
  'border--focus--color': GreyPalette['400'],
  'link--color': '#3B82F6',
  // THEME_TOKENS.base.color.action
  'link--hover--color': '#3B82F6',
  // THEME_TOKENS.base.color.action
  'linkSecondary--color': 'white',
  // previously used lightBuffYellow: '#fff0bb'

  'pageHeaderText--color': GreyPalette['900'],
  // THEME_TOKENS.base.color.primaryText
  'pageHeaderBackground--color': 'white',
  'headings--color': GreyPalette['900'],
  // THEME_TOKENS.base.color.primaryText
  'textBody--color': GreyPalette['900'],
  // THEME_TOKENS.base.color.primaryText

  'headings--fontFamily': '"Source Sans Pro", system-ui, Arial, sans-serif',
  'textBody--fontFamily': '"Source Sans Pro", system-ui, Arial, sans-serif',
  'spacerXS--size': '2px',
  'spacerSM--size': '4px',
  'spacerMD--size': '8px',
  'spacerLG--size': '16px',
  'spacerXL--size': '32px',
  /**
   * Specifies responsive breakpoints (should be specified as an int not a string)
   */
  'breakpointSM--size': breakpoint.small,
  'breakpointMD--size': breakpoint.medium,
  'breakpointLG--size': breakpoint.large,
  'breakpointXL--size': breakpoint.xlarge,
  'borderRadius--size': '4px',
  // Trip Details contact section
  'icon--contact--bgcolor': GreyPalette['300'],
  'icon--air--color': ColorPalette.cgBlue,
  'icon--air--bgcolor': ColorPalette.paleCerulean,
  'icon--car--color': ColorPalette.mantis,
  'icon--car--bgcolor': ColorPalette.teaGreen,
  'icon--country--color': ColorPalette.blue,
  'icon--country--bgcolor': ColorPalette.lightBlue,
  'icon--fee--color': ColorPalette.cyan,
  'icon--fee--bgcolor': ColorPalette.azureishWhite,
  'icon--hotel--color': ColorPalette.cherry,
  'icon--hotel--bgcolor': ColorPalette.nadeshikoPink,
  'icon--mile--color': ColorPalette.liberty,
  'icon--mile--bgcolor': ColorPalette.lavenderGray,
  'icon--night--color': ColorPalette.liberty,
  'icon--night--bgcolor': ColorPalette.lavenderGray,
  'icon--policy--color': ColorPalette.amazonite,
  'icon--policy--bgcolor': ColorPalette.paleTurquoise,
  'icon--money--color': ColorPalette.saffron,
  'icon--money--bgcolor': ColorPalette.blond,
  'icon--ticket--color': ColorPalette.green,
  'icon--ticket--bgcolor': ColorPalette.seaFoamGreen,
  'icon--trip--color': ColorPalette.orange,
  'icon--trip--bgcolor': ColorPalette.peachPuff,
  'icon--co2--color': ColorPalette.mantis,
  'icon--co2--bgcolor': ColorPalette.teaGreen,
  'icon--default--color': ColorPalette.blue,
  'icon--default--bgcolor': ColorPalette.lightBlue
};

var BaseButton = globalapi => ({
  'text--fontFamily': globalapi['textBody--fontFamily'],
  'primary--color': globalapi['link--color'],
  // THEME_TOKENS.base.color.action
  'secondary--color': globalapi['secondary--color'],
  'tertiary--color': globalapi['tertiary--color'],
  'disabled--color': globalapi['disabled--color'],
  uiDesign: 'standard',
  'borderRadius--size': globalapi['borderRadius--size'],
  'borderWidth--standard': '1px',
  'inlineFormText--size': '14px',
  borderTransparent: false
});

var BaseCard = globalapi => ({
  'subheaderBackground--color': globalapi['light--color'],
  'backgroundPrimary--color': 'white',
  'backgroundSecondary--color': 'rgba(255, 255, 255, 0.5)',
  'borderFlush--width': '5px 0 1px',
  'borderSmall--width': '0',
  'borderMedium--width': '0',
  'border--color': globalapi['border--color'],
  'box-shadow': 'none'
});

var BaseChat = globalapi => ({
  'minimized-chat--color': globalapi['link--color'],
  'minimized-icon--color': 'white'
});

var BaseImages = {
  'trip-details-sidebar-seats': 'https://cdn.amtrav.com/trip-details/sidebar/a2b-seats.svg',
  'trip-details-sidebar-hotel': 'https://cdn.amtrav.com/trip-details/sidebar/a2b-hotel.svg',
  'trip-details-sidebar-car': 'https://cdn.amtrav.com/trip-details/sidebar/a2b-car.svg',
  'trip-details-sidebar-insurance': 'https://cdn.amtrav.com/trip-details/sidebar/insurance.png'
};

var BaseSvgPalette = globalapi => ({
  'primary--color': globalapi['primary--color'],
  'secondary--color': globalapi['secondary--color'],
  'tertiary--color': globalapi['tertiary--color'],
  'accent--color': globalapi['light--color'],
  'border--color': globalapi['dark--color']
});

var BaseTextfield = globalapi => ({
  'border--color': globalapi['border--color'],
  'border--focus--color': globalapi['border--focus--color'],
  'text--color': globalapi['textBody--color'],
  'background--color': globalapi['light--color'],
  'background--focus--color': globalapi['light--color'],
  // minHeight ends up being a string when used in TextInput & DropdownSelect
  'minHeight--size': 30,
  'dropdownMinHeight--size': 30,
  // prominent
  'prominentText--color': globalapi['textBody--color'],
  'prominentBackground--color': globalapi['light--color'],
  'prominentBackground--focus--color': globalapi['light--color'],
  // options
  enableInnerShadow: false,
  // box-shadow
  'box-shadow': 'none',
  // border-width
  'borderWidth--standard': '1px',
  'borderRadius--size': globalapi['borderRadius--size']
});

var BaseTable = globalapi => ({
  'primaryBackground--color': globalapi['light--color']
});

var BaseLoader = globalapi => ({
  'loadingBar--color': globalapi['primary--color'],
  kind: null,
  withCard: true
});

var BaseSiteNavigation = () => ({
  navLogo: '',
  // if showNavIcons stays false, there is no need for an activeLink color in that theme
  'activeLink--color': '#208dcc',
  // ColorPalette.blue
  'sectionTitle--color': '#208dcc',
  // ColorPalette.blue
  'menuItemText--color': 'white',
  'navBorder--color': 'rgba(0, 0, 0, 0.17)',
  'contactBackground--color': 'rgba(0, 0, 0, .4)',
  'helpCenterBackground--color': 'rgba(255, 255, 255, 0.3)',
  'helpCenterText--color': '#fff',
  'hamburgerMobile--color': '#fff',
  showPoweredBy: false,
  showNavIcons: false,
  showBlogLink: true
});

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

// These icons & color tokens are only used in the Reports charts & graphs
// There is no need to move these color tokens to the new theme-system

var generateIconLookup = globals => (concept, overrides) => {
  const conceptLookup = {
    air: {
      color: globals['icon--air--color'],
      backgroundColor: globals['icon--air--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconJet, _extends({}, props, overrides, {
        color: globals['icon--air--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconJet, _extends({}, props, overrides, {
        color: globals['icon--air--color'],
        backgroundColor: globals['icon--air--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconJet, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--air--color']
      }))
    },
    car: {
      color: globals['icon--car--color'],
      backgroundColor: globals['icon--car--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconCar, _extends({}, props, overrides, {
        color: globals['icon--car--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconCar, _extends({}, props, overrides, {
        color: globals['icon--car--color'],
        backgroundColor: globals['icon--car--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconCar, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--car--color']
      }))
    },
    countr: {
      color: globals['icon--country--color'],
      backgroundColor: globals['icon--country--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconEarth, _extends({}, props, overrides, {
        color: globals['icon--country--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconEarth, _extends({}, props, overrides, {
        color: globals['icon--country--color'],
        backgroundColor: globals['icon--country--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconEarth, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--country--color']
      }))
    },
    fee: {
      color: globals['icon--fee--color'],
      backgroundColor: globals['icon--fee--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: globals['icon--fee--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: globals['icon--fee--color'],
        backgroundColor: globals['icon--fee--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--fee--color']
      }))
    },
    hotel: {
      color: globals['icon--hotel--color'],
      backgroundColor: globals['icon--hotel--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconHotel, _extends({}, props, overrides, {
        color: globals['icon--hotel--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconHotel, _extends({}, props, overrides, {
        color: globals['icon--hotel--color'],
        backgroundColor: globals['icon--hotel--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconHotel, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--hotel--color']
      }))
    },
    mile: {
      color: globals['icon--mile--color'],
      backgroundColor: globals['icon--mile--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconSpeedometer, _extends({}, props, overrides, {
        color: globals['icon--mile--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconPlane, _extends({}, props, overrides, {
        color: globals['icon--mile--color']
        // backgroundColor={globals['icon--mile--bgcolor']}
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconSpeedometer, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--mile--color']
      }))
    },
    night: {
      color: globals['icon--night--color'],
      backgroundColor: globals['icon--night--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconHalfmoon, _extends({}, props, overrides, {
        color: globals['icon--night--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconHalfmoon, _extends({}, props, overrides, {
        color: globals['icon--night--color'],
        backgroundColor: globals['icon--night--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconHalfmoon, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--night--color']
      }))
    },
    policy: {
      color: globals['icon--policy--color'],
      backgroundColor: globals['icon--policy--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconCheckCurved, _extends({}, props, overrides, {
        color: globals['icon--policy--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconCheckCurved, _extends({}, props, overrides, {
        color: globals['icon--policy--color'],
        backgroundColor: globals['icon--policy--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconCheckCurved, _extends({}, props, overrides, {
        color: "white",
        backgourndColor: globals['icon--policy--color']
      }))
    },
    price: {
      color: globals['icon--money--color'],
      backgroundColor: globals['icon--money--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: globals['icon--money--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: globals['icon--money--color'],
        backgroundColor: globals['icon--money--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--money--color']
      }))
    },
    spend: {
      color: globals['icon--money--color'],
      backgroundColor: globals['icon--money--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: globals['icon--money--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: globals['icon--money--color'],
        backgroundColor: globals['icon--money--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconMoney, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--money--color']
      }))
    },
    ticket: {
      color: globals['icon--ticket--color'],
      backgroundColor: globals['icon--ticket--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconJet, _extends({
        rotate: 45
      }, props, overrides, {
        color: globals['icon--ticket--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconJet, _extends({
        rotate: 45
      }, props, overrides, {
        color: globals['icon--ticket--color'],
        backgroundColor: globals['icon--ticket--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconJet, _extends({
        rotate: 45
      }, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--ticket--color']
      }))
    },
    trip: {
      color: globals['icon--trip--color'],
      backgroundColor: globals['icon--trip--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconTrips, _extends({}, props, overrides, {
        color: globals['icon--trip--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconTrips, _extends({}, props, overrides, {
        color: globals['icon--trip--color'],
        backgroundColor: globals['icon--trip--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconTrips, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--trip--color']
      }))
    },
    co2: {
      color: globals['icon--co2--color'],
      backgroundColor: globals['icon--co2--bgcolor'],
      icon: props => /*#__PURE__*/React.createElement(IconEco, _extends({}, props, overrides, {
        color: globals['icon--co2--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconEco, _extends({}, props, overrides, {
        color: globals['icon--co2--color']
        // backgroundColor={globals['icon--co2--bgcolor']}
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconTrips, _extends({}, props, overrides, {
        color: "white",
        backgroundColor: globals['icon--trip--color']
      }))
    },
    default: {
      color: globals['icon--default--color'],
      backgroundColor: globals['icon--default--color'],
      icon: props => /*#__PURE__*/React.createElement(IconInfo, _extends({}, props, overrides, {
        color: globals['icon--default--color']
      })),
      iconTwoTone: props => /*#__PURE__*/React.createElement(IconInfo, _extends({}, props, overrides, {
        color: globals['icon--default--color'],
        backgroundColor: globals['icon--default--bgcolor']
      })),
      iconInverted: props => /*#__PURE__*/React.createElement(IconInfo, _extends({}, props, overrides, {
        color: globals['icon--default--color']
      }))
    }
  };
  conceptLookup.road = conceptLookup.car;
  const found = Object.keys(conceptLookup).find(key => {
    const regex = RegExp(key);
    return regex.test(concept.toLowerCase());
  });
  return found ? conceptLookup[found] : conceptLookup.default;
};

var BaseBreakpoints = globalapi => [`${globalapi['breakpointSM--size']}px`, `${globalapi['breakpointMD--size']}px`, `${globalapi['breakpointLG--size']}px`, `${globalapi['breakpointXL--size']}px`];

const BaseTheme = {
  brand: 'base',
  brandName: 'Our Site',
  logoInterstitial: '',
  loader: BaseLoader(BaseGlobal),
  global: BaseGlobal,
  button: BaseButton(BaseGlobal),
  card: BaseCard(BaseGlobal),
  chat: BaseChat(BaseGlobal),
  svgPalette: BaseSvgPalette(BaseGlobal),
  textfield: BaseTextfield(BaseGlobal),
  table: BaseTable(BaseGlobal),
  greyPalette: GreyPalette,
  // These breakpoints are used by rebass https://github.com/rebassjs/grid#theming
  breakpoints: BaseBreakpoints(BaseGlobal),
  siteNavigation: BaseSiteNavigation(),
  stylesForConcept: generateIconLookup(BaseGlobal),
  images: BaseImages
};

export { BaseTheme as B, GreyPalette as G, _extends as _, BaseGlobal as a, BaseLoader as b, BaseButton as c, BaseCard as d, BaseChat as e, BaseSvgPalette as f, BaseTable as g, BaseTextfield as h, BaseBreakpoints as i, BaseImages as j, BaseSiteNavigation as k, generateIconLookup as l };
//# sourceMappingURL=base-57e9514e.js.map
